import { useFiMerchant } from 'hooks/useFiMerchant';
import styles from './CheckoutOtpPage.module.scss';
import { TextV2 } from 'components/TextV2/TextV2';
import { FormWrapper } from 'components/Form/FormWrapper';
import FormControl from '@material-ui/core/FormControl';
import { Controller, useForm } from 'react-hook-form';
import TextField from '@material-ui/core/TextField';
import { SproutButton } from 'components/Button/SproutButton';
import { LinkVariant } from 'api/contentful/contentTypes.cloverdotcom';
import { FooterLinks } from 'components/Checkout/CheckoutEmailAndInfoPage/FooterLinks/FooterLinks';
import React, { useCallback, useMemo, useState, FC } from 'react';
import { useTranslation } from 'react-i18next';
import { CHECKOUT_OTP_PAGE_TEST_ID } from './CheckoutOtpPage.const';
import { useCheckout } from 'components/Checkout/CheckoutProvider';
import { getOtp, validateOtp } from 'services/shop/checkout';
import { Redirect, useHistory } from 'react-router-dom';
import { IBannerProps, SproutBanner } from 'components/SproutBanner/SproutBanner';
import { datadogRum } from '@datadog/browser-rum';
import { Button } from 'components/Button/Button';
import { redesignV2RoutePath } from 'constants/redesignV2';
import { CheckoutRoutePath } from '../Checkout';
import { formatPhoneNumberWithNonBreakingSpace } from 'utils/phoneNumberUtils';
import { defaultErrorState } from '../Utils';

const FORM_ID = 'otp-form';

interface ICheckoutOtpFields {
  passcode: string;
}

interface ICheckoutOtpPageProps {
  isMaxPhoneAttempts: boolean;
  setIsMaxOtpRequests: (value: boolean) => void;
}

export const CheckoutOtpPage: FC<ICheckoutOtpPageProps> = ({ setIsMaxOtpRequests, isMaxPhoneAttempts }) => {
  const { t } = useTranslation();
  const { userInfo, leadId, errorState, setErrorState, setUserInfo } = useCheckout();
  const [otpAttemptsRemaining, setOtpAttemptsRemaining] = useState(2);
  const [isExpired, setIsExpired] = useState(false);
  const [isValidated, setIsValidated] = useState(false);
  const [isNewOtpSuccessfullyRequested, setIsNewOtpSuccessfullyRequested] = useState(false);
  const { subscriberRefId } = useFiMerchant();
  const history = useHistory();
  const formattedPhoneNumber = formatPhoneNumberWithNonBreakingSpace(userInfo?.phoneNumber);

  const {
    setValue,
    control,
    handleSubmit,
    formState: { errors, isSubmitSuccessful },
  } = useForm<ICheckoutOtpFields>({
    mode: 'onSubmit',
    criteriaMode: 'firstError',
    defaultValues: {
      passcode: '',
    },
  });

  const bannerProps = useMemo((): IBannerProps | null => {
    if (isNewOtpSuccessfullyRequested) {
      return {
        bannerText: t('checkout-otp-page.code-sent'),
        variant: 'success',
      };
    }
    if (otpAttemptsRemaining == 0) {
      return {
        bannerText: t('checkout-otp-page.out-of-code-attempts'),
        variant: 'error',
      };
    }
    if (isExpired) {
      return {
        bannerText: t('checkout-otp-page.code-has-expired'),
        variant: 'error',
      };
    }
    if (isSubmitSuccessful && !errorState.hasApiError && !isValidated) {
      return {
        bannerText: t('checkout-otp-page.invalid-code', { count: otpAttemptsRemaining }),
        variant: 'error',
      };
    }
    return null;
  }, [
    otpAttemptsRemaining,
    isExpired,
    isSubmitSuccessful,
    errorState.hasApiError,
    isValidated,
    isNewOtpSuccessfullyRequested,
    t,
  ]);

  const onSubmit = async (values: ICheckoutOtpFields) => {
    let data;
    try {
      const response = await validateOtp(leadId, values.passcode);
      data = response.data;
    } catch (e) {
      datadogRum.addError(e, { leadId });
      setErrorState({
        ...defaultErrorState,
        hasApiError: true,
      });
      return;
    }

    if (data.valid) {
      setIsValidated(true);
      const baseRedirectUrl = `/apply/create-account?leadUuid=${leadId}`;
      const queryParamToAdd = subscriberRefId ? `&subscriberRefId=${subscriberRefId}` : '';

      window.location.replace(baseRedirectUrl + queryParamToAdd);
      return;
    }

    setIsNewOtpSuccessfullyRequested(false);
    setIsExpired(data.expired);
    setOtpAttemptsRemaining(data.validationAttemptsRemaining);

    const hasExhaustedOtpAttempts =
      data.otpRequestsRemaining == 0 && (data.validationAttemptsRemaining == 0 || data.expired);
    if (hasExhaustedOtpAttempts && isMaxPhoneAttempts) {
      setErrorState({
        ...defaultErrorState,
        isLockedOut: true,
      });
      history.push(redesignV2RoutePath.PURCHASE + CheckoutRoutePath.OTP_UNSUCCESSFUL);
      return;
    }

    if (hasExhaustedOtpAttempts) {
      setIsMaxOtpRequests(true);
      userInfo && setUserInfo({ ...userInfo, phoneNumber: '' });
      history.push(redesignV2RoutePath.PURCHASE + CheckoutRoutePath.MOBILE_PHONE);
    }
  };

  const fetchAnotherOtp = useCallback(async () => {
    setValue('passcode', '');
    try {
      await getOtp(leadId);
    } catch (e) {
      datadogRum.addError(e, { leadId });
      if (e.response.status === 429) {
        setErrorState({
          ...defaultErrorState,
          isLockedOut: true,
        });
        history.push(redesignV2RoutePath.PURCHASE + CheckoutRoutePath.OTP_UNSUCCESSFUL);
        return;
      }
      setErrorState({
        ...defaultErrorState,
        hasApiError: true,
      });
      return;
    }
    setIsNewOtpSuccessfullyRequested(true);
  }, [setValue, leadId, setErrorState, history]);

  if (!leadId) {
    return <Redirect to={redesignV2RoutePath.CART} />;
  }

  return (
    <section className={styles.checkoutOtpPage}>
      <header className={styles.header}>
        <TextV2
          tagName="h2"
          variant="h2-bold"
          value={t('checkout-otp-page.title', { phoneNumber: formattedPhoneNumber })}
        />
        <div className={styles.subtitleContainer}>
          <TextV2
            className={styles.subtitle}
            tagName="h5"
            variant="body"
            value={t('checkout-otp-page.subtitle')}
          >
            <Button variant={LinkVariant.TEXT} className={styles.sendAnotherCode} onClick={fetchAnotherOtp}>
              {t('checkout-otp-page.send-another-code')}
            </Button>
          </TextV2>
        </div>
        {bannerProps && <SproutBanner {...bannerProps} />}
      </header>
      <div className={styles.requiredFieldWrapper}>
        <TextV2 tagName="h5" variant="body-small" value={t('checkout-otp-page.required-field')} />
      </div>
      <section className={styles.formContainer}>
        <FormWrapper>
          <FormControl fullWidth>
            <form id={FORM_ID} onSubmit={handleSubmit(onSubmit)}>
              <div className={styles.inputGroup}>
                <TextV2
                  className={styles.formHeading}
                  tagName="h5"
                  variant="h5-medium"
                  value={t('checkout-otp-page.info-title')}
                />
                <Controller
                  name="passcode"
                  control={control}
                  rules={{
                    required: t('oneTimePasscode.required'),
                  }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      autoFocus
                      id="passcode"
                      inputProps={{ 'data-testid': CHECKOUT_OTP_PAGE_TEST_ID.passcode }}
                      name="passcode"
                      label={`${t('checkout-otp-page.passcode-label')}`}
                      helperText={errors?.passcode?.message}
                      error={!!errors?.passcode}
                      variant="outlined"
                    />
                  )}
                />
              </div>
            </form>
          </FormControl>
        </FormWrapper>
      </section>
      <div>
        <SproutButton
          data-testid={CHECKOUT_OTP_PAGE_TEST_ID.submitButton}
          type="submit"
          variant={LinkVariant.PRIMARY}
          form={FORM_ID}
          className={styles.submitButton}
        >
          {t('continue')}
        </SproutButton>
      </div>
      <FooterLinks />
    </section>
  );
};
